import { useAtom } from "jotai";
import { useState } from "react";
import {
  Checkbox,
  Color,
  Flex,
  Grid,
  Icon,
  Select,
  SmallNumberInput,
  SubgridTable,
  Text,
} from "src/elements";

import { planogramSelectedItemsAtom } from "src/components/PlanogramExtensions/store/atoms";
import { formatDimension } from "src/utils/formatDimension";
import {
  PlanogramItem,
  PlanogramItemCommonNonCircular,
  PlanogramItemOrientation,
  UnrangedPlanogramItem,
} from "@CommonTypes/merchflow/pog/item";
import { usePogActions } from "src/components/Planogram/store/actions";
import { useTranslation } from "src/i18n";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { ProductDetailsCommon } from "../components/ProductDetailsCommon";
import { stampedWeightStyles } from "src/components/Planogram/common/shelfWeight";
import {
  isPegboardPlacementSelection,
  isPlanogramItemSelection,
} from "src/components/Planogram/common/planogramSelection";
import {
  PLANOGRAM_ORIENTATION_MIXED,
  getCappingOrientationOptions,
  getPlanogramOrientationSelectState,
} from "../components/planogramOrientation";

type MerchandiseStyle = "unit" | "tray" | "case";

const merchandiseStyles: MerchandiseStyle[] = ["unit", "tray", "case"];

const merchandiseStyleMapping: Record<MerchandiseStyle, string> = {
  unit: "Unit",
  tray: "Tray",
  case: "Case",
};

const generateData = (pogItem: PlanogramItem | UnrangedPlanogramItem) => {
  return merchandiseStyles.map((style) => {
    const {
      squeezeHeightPct,
      squeezeWidthPct,
      squeezeDepthPct,
      squeezeWidth,
      squeezeHeight,
      squeezeDepth,
    } = pogItem;
    return {
      style: merchandiseStyleMapping[style],
      width: (
        (squeezeWidth ? squeezeWidthPct : 1) * formatDimension(pogItem[`${style}Width`])
      ).toFixed(2),
      height: (
        (squeezeHeight ? squeezeHeightPct : 1) * formatDimension(pogItem[`${style}Height`])
      ).toFixed(2),
      depth: (
        (squeezeDepth ? squeezeDepthPct : 1) * formatDimension(pogItem[`${style}Depth`])
      ).toFixed(2),
      units:
        style === "unit" ? 1 : style === "tray" ? pogItem.noOfUnitsInTray : pogItem.noOfUnitsInCase,
    };
  });
};

interface Props {
  pog: Pog;
  setPog: React.Dispatch<React.SetStateAction<Pog>>;
}

export const ProductDetails = ({ pog, setPog }: Props) => {
  const { t } = useTranslation("common");
  const {
    setCapping,
    setHorizontalFacings,
    setMerchandisingStyle,
    setOrientation,
    setSqueezeDimension,
    setVerticalFacings,
    saveChanges,
  } = usePogActions({
    pog,
    setPog,
  });

  const [selectedItems] = useAtom(planogramSelectedItemsAtom);
  const [isCappingExpanded, setIsCappingExpanded] = useState(false);

  const selectedShelfItems = selectedItems.filter(isPlanogramItemSelection);
  const selectedItem = selectedItems[0];

  const cdtSourceItems = pog.planogram.getAllPlanogramProducts();
  const weightsTrusted = stampedWeightStyles(pog).size === 0;

  if (!selectedItem) {
    return null;
  }

  if (isPegboardPlacementSelection(selectedItem)) {
    const selectedProduct = selectedItem.pegProduct;

    return (
      <ProductDetailsCommon
        item={selectedProduct}
        selectedItems={[selectedProduct]}
        cdtSourceItems={cdtSourceItems}
        saveChanges={saveChanges}
        statedWeightsTrusted={weightsTrusted}
      />
    );
  }

  const shelfItem = selectedItem;

  const orientationSelectState = getPlanogramOrientationSelectState([shelfItem]);
  const cappingOrientationOptions = getCappingOrientationOptions(shelfItem.orientation);
  const firstCappingOrientation = cappingOrientationOptions[0]?.value;
  const getCappingOrientationValue = (orientation: PlanogramItemOrientation) =>
    cappingOrientationOptions.some((option) => option.value === orientation)
      ? orientation
      : (firstCappingOrientation ?? orientation);

  const cappingIndicator = [
    shelfItem.xCapNum > 0 && `X:${shelfItem.xCapNum}`,
    shelfItem.yCapNum > 0 && `Y:${shelfItem.yCapNum}`,
    shelfItem.zCapNum > 0 && `Z:${shelfItem.zCapNum}`,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <ProductDetailsCommon
      item={shelfItem}
      selectedItems={selectedShelfItems}
      cdtSourceItems={cdtSourceItems}
      saveChanges={saveChanges}
      statedWeightsTrusted={weightsTrusted}
      beforeCdt={
        <Flex column gap="10px">
          <Text variant="small1" color={Color.primary}>
            {t("planogram.facingsAndPlacement")}
          </Text>

          <Grid columns="1fr 1fr" gap="10px">
            <SmallNumberInput
              label="Horizontal Facings:"
              variant="sidebuttons"
              textVariant="caption1"
              textColor={Color.primaryActive}
              width="20px"
              value={shelfItem.facings / shelfItem.facingsRows}
              setValue={(horizontalFacings) => setHorizontalFacings([shelfItem], horizontalFacings)}
              min={1}
            />

            <SmallNumberInput
              label="Vertical Facings:"
              variant="sidebuttons"
              textVariant="caption1"
              textColor={Color.primaryActive}
              width="20px"
              value={shelfItem.facingsRows}
              setValue={(verticalFacings) => setVerticalFacings([shelfItem], verticalFacings)}
              min={1}
            />
          </Grid>

          <Flex gap="10px" justify="space-between">
            <Text variant="caption1" color={Color.primaryActive}>
              Squeeze:
            </Text>

            <Flex gap="8px">
              <Checkbox
                isCheckboxLeft
                label="Width"
                isChecked={shelfItem.squeezeWidth}
                size="small"
                setIsChecked={(checked) =>
                  setSqueezeDimension([shelfItem], "squeezeWidth", checked)
                }
              />

              <Checkbox
                isCheckboxLeft
                label="Height"
                isChecked={shelfItem.squeezeHeight}
                size="small"
                setIsChecked={(checked) =>
                  setSqueezeDimension([shelfItem], "squeezeHeight", checked)
                }
              />

              <Checkbox
                isCheckboxLeft
                label="Depth"
                isChecked={shelfItem.squeezeDepth}
                size="small"
                setIsChecked={(checked) =>
                  setSqueezeDimension([shelfItem], "squeezeDepth", checked)
                }
              />
            </Flex>
          </Flex>

          <Flex gap="10px" justify="between" align="center">
            <Text variant="caption1" color={Color.primaryActive}>
              Merchandise Style:
            </Text>

            <Select
              width="min-width"
              size="small"
              options={
                [
                  { label: "UNIT", value: "UNIT" },
                  { label: "TRAY", value: "TRAY" },
                  { label: "CASE", value: "CASE" },
                ] satisfies {
                  label: string;
                  value: PlanogramItemCommonNonCircular["merchandisingStyle"];
                }[]
              }
              value={shelfItem.merchandisingStyle}
              setValue={(merchandisingStyle) =>
                setMerchandisingStyle([shelfItem], merchandisingStyle)
              }
            />
          </Flex>

          <Flex gap="10px" justify="between" align="center">
            <Text variant="caption1" color={Color.primaryActive}>
              Orientation:
            </Text>

            <Select
              width="min-width"
              size="small"
              options={orientationSelectState.options}
              value={orientationSelectState.value}
              isDisabled={orientationSelectState.isDisabled}
              setValue={(orientation) => {
                if (orientation !== PLANOGRAM_ORIENTATION_MIXED) {
                  setOrientation([shelfItem], orientation);
                }
              }}
            />
          </Flex>
        </Flex>
      }
    >
      <Grid columns="1fr 1fr" gap="10px" margin="10px 0 0 0">
        <SmallNumberInput
          label="Min. on Shelf:"
          variant="sidebuttons"
          textVariant="caption1"
          textColor={Color.primaryActive}
          width="20px"
          value={shelfItem.minOnShelf}
          setValue={(value) => {
            shelfItem.minOnShelf = value;
            saveChanges();
          }}
          min={0}
        />

        <Flex />

        <SmallNumberInput
          label="Min. DoS:"
          variant="sidebuttons"
          textVariant="caption1"
          textColor={Color.primaryActive}
          width="20px"
          value={shelfItem.minDaysOfSupply}
          setValue={(value) => {
            shelfItem.minDaysOfSupply = value;
            saveChanges();
          }}
          min={0}
        />

        <SmallNumberInput
          label="Max. DoS:"
          variant="sidebuttons"
          textVariant="caption1"
          textColor={Color.primaryActive}
          width="20px"
          value={shelfItem.maxDaysOfSupply}
          setValue={(value) => {
            shelfItem.maxDaysOfSupply = value;
            saveChanges();
          }}
        />
      </Grid>

      <Flex
        column
        border={`1px solid ${Color.spaceGray}`}
        borderRadius="4px"
        margin="10px 0 0 0"
        overflow="hidden"
      >
        <Flex
          padding="8px 12px"
          background={Color.lightGray}
          backgroundHovered={Color.lightGrayHover}
          cursor="pointer"
          justify="space-between"
          align="center"
          onClick={() => setIsCappingExpanded(!isCappingExpanded)}
        >
          <Flex gap="8px" align="center">
            <div
              style={{
                transform: isCappingExpanded ? "rotate(90deg)" : "rotate(0deg)",
                display: "inline-flex",
                transition: "transform 0.2s ease",
              }}
            >
              <Icon name="chevronRight" size={12} color={Color.primaryActive} />
            </div>
            <Text variant="caption1" color={Color.primaryActive}>
              Capping & Capacity
            </Text>
          </Flex>

          {cappingIndicator && !isCappingExpanded && (
            <Flex
              background={Color.greenSmoke}
              padding="2px 6px"
              borderRadius="10px"
              align="center"
              justify="center"
            >
              <Text variant="caption3" color={Color.primaryActive}>
                {cappingIndicator}
              </Text>
            </Flex>
          )}
        </Flex>

        {isCappingExpanded && (
          <Flex column padding="12px" gap="12px" background={Color.white}>
            <Grid columns="1.2fr 1fr 1.5fr" gap="8px" align="center">
              <Text variant="caption1" color={Color.textSecondary}>
                X Capping:
              </Text>
              <SmallNumberInput
                label=""
                variant="sidebuttons"
                textVariant="caption1"
                textColor={Color.primaryActive}
                width="20px"
                value={shelfItem.xCapNum}
                setValue={(val) => {
                  const xCapOrientation = getCappingOrientationValue(shelfItem.xCapOrientation);
                  setCapping([shelfItem], {
                    xCapNum: val,
                    ...(val > 0 && xCapOrientation !== shelfItem.xCapOrientation
                      ? { xCapOrientation }
                      : {}),
                  });
                }}
                min={0}
              />
              <Select
                width="100%"
                size="small"
                options={cappingOrientationOptions}
                value={getCappingOrientationValue(shelfItem.xCapOrientation)}
                setValue={(val) => setCapping([shelfItem], { xCapOrientation: val })}
              />
            </Grid>

            <Grid columns="1.2fr 1fr 1.5fr" gap="8px" align="center">
              <Text variant="caption1" color={Color.textSecondary}>
                Y Capping:
              </Text>
              <SmallNumberInput
                label=""
                variant="sidebuttons"
                textVariant="caption1"
                textColor={Color.primaryActive}
                width="20px"
                value={shelfItem.yCapNum}
                setValue={(val) => {
                  const yCapOrientation = getCappingOrientationValue(shelfItem.yCapOrientation);
                  setCapping([shelfItem], {
                    yCapNum: val,
                    ...(val > 0 && yCapOrientation !== shelfItem.yCapOrientation
                      ? { yCapOrientation }
                      : {}),
                  });
                }}
                min={0}
              />
              <Select
                width="100%"
                size="small"
                options={cappingOrientationOptions}
                value={getCappingOrientationValue(shelfItem.yCapOrientation)}
                setValue={(val) => setCapping([shelfItem], { yCapOrientation: val })}
              />
            </Grid>

            <Grid columns="1.2fr 1fr 1.5fr" gap="8px" align="center">
              <Text variant="caption1" color={Color.textSecondary}>
                Z Capping:
              </Text>
              <SmallNumberInput
                label=""
                variant="sidebuttons"
                textVariant="caption1"
                textColor={Color.primaryActive}
                width="20px"
                value={shelfItem.zCapNum}
                setValue={(val) => {
                  const zCapOrientation = getCappingOrientationValue(shelfItem.zCapOrientation);
                  setCapping([shelfItem], {
                    zCapNum: val,
                    ...(val > 0 && zCapOrientation !== shelfItem.zCapOrientation
                      ? { zCapOrientation }
                      : {}),
                  });
                }}
                min={0}
              />
              <Select
                width="100%"
                size="small"
                options={cappingOrientationOptions}
                value={getCappingOrientationValue(shelfItem.zCapOrientation)}
                setValue={(val) => setCapping([shelfItem], { zCapOrientation: val })}
              />
            </Grid>
          </Flex>
        )}
      </Flex>

      <Flex margin="10px 0 0 0">
        <SubgridTable
          columns={[
            { id: "style", header: "Style" },
            { id: "height", header: "Height", justify: "right" },
            { id: "width", header: "Width", justify: "right" },
            { id: "depth", header: "Depth", justify: "right" },
            { id: "units", header: "No. Of Units", justify: "right" },
          ]}
          data={generateData(shelfItem)}
        />
      </Flex>
    </ProductDetailsCommon>
  );
};
